import { yupResolver } from '@hookform/resolvers/yup'
import { display, margin, width } from '@mui/system'
import moment from 'moment/moment'
import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row
} from 'reactstrap'
import ResponsiveDatePickers from '../../../components/date-picker/index'
import { InputEmail, InputNumber, InputPhone, InputText } from '../../../components/inputs'
import AreaFilter from '../../area/components/filter-area/area-filter'
import { getTransactions, setInitialData, setProductsCreditAction, setUserAction } from '../store'
import { SchemaValidations } from './validations'
import { sendFiltersAdapter } from '../adapters'
import { invoicesdb, invoicesNotesdb } from '../db/invoices'
import { departmentsOptions } from '../db/departments'
import { municipalityOptions } from '../db/municipalities'
import { districtsOptions } from '../db/districts'
import { economicActivity } from '../db/economicActivity'
import { clientsdb } from '../db/users'
import toast from 'react-hot-toast'
import SearchInput from './searchInput'
import { addUserAction, editUserAction, getData } from '../../customer-master/store'
import { taxCreditdb } from '../db/taxCredit'
import { useParams } from 'react-router-dom'
import { addClient, editClient } from '../../customer-master/thunks'
import { getDocuments, setProductsCreditJsonList, setProductsCreditList, setRelatedDoc } from '../thunks'
import { formatDateToYMD } from '../../../../utility/utils'
import Cleave from 'cleave.js/react'
import classnames from 'classnames'
import InputInternationalPhone from '../../../components/inputs/internationalPhone'
import { contributorTypedb } from '../../customer-master/db/contributorType'

// import InputEmail from '../../../components/inputs'

const initialFilterProps = {
  clientCode: '',
  codeType: '',
  dteType: '',
  company: '',
  receiverEmail: '',
  receiverPhoneNumber: '',
  receiverName: '',
  startDate: moment().subtract(1, 'day'),
  endDate: null,
  address: '',
  department: null,
  municipality: null,
  district: null,
  DUI: '',
  NIT: '',
  NRC: '',
  dui: '',
  nit: '',
  nrc: '',
  EconomicActivity: null,
  economicActivity: null,
  tradeName: '',
  largeConsumer: null,
  dteClientType: null,
  taxCredit: null,
  taxCreditR: null

}

const CreateUserCreditDebitForm = forwardRef(( onFilter, ref ) => {
  const [filterProps, setFilterProps] = useState(initialFilterProps)
  const [areaFilterValue, setAreaFilterValue] = useState(null)
  const [NITValue, setNITValue] = useState('')
  const [nameValue, setNameValue] = useState('')
  const [isEdited, setIsEdited] = useState(false)
  const [isNit, setNit] = useState(false)
  const [clientId, setClientId] = useState(null)


  const dispatch = useDispatch()

  const { isPending } = useSelector((state) => state.transactions)
  const { data } = useSelector(state => state.customerMaster)
  const { departments, cities, districts, economicActivities, documentTypes, documentList, receptor, taxpayerTypes, personTypes } = useSelector(state => state.invoiceIssuance)

  // CAT-091 desde el backend: sus id cambian entre ambientes, así que la lista
  // estática solo sirve de respaldo para no dejar el select vacío
  const hayTaxpayerTypes = Array.isArray(taxpayerTypes) && taxpayerTypes.length > 0
  const contributorOptions = hayTaxpayerTypes ? taxpayerTypes : contributorTypedb
  const otroContributorId = contributorOptions.find(item => item.value === '04')?.id
  // El campo "Tipo de contribuyente" solo se muestra para CCF (03); para el
  // resto el valor sale del default. Además, los clientes antiguos pueden
  // traer guardado un valor que no pertenece a CAT-091 (se guardaban países).
  // Por eso solo se acepta lo que exista en el catálogo: cualquier otra cosa
  // cae a "Otro", que es el valor por defecto correcto
  const idContribuyente = (seleccion) => {
    const enCatalogo = contributorOptions.find(item => item.value === seleccion?.value)
    return Number(enCatalogo?.id ?? otroContributorId) || null
  }
  const hayPersonTypes = Array.isArray(personTypes) && personTypes.length > 0
  const idTipoPersona = (codigo) => Number(personTypes.find(item => item.value === codigo)?.id) || null
  // CAT-019 code 10005 = "Otros": respaldo cuando no se elige actividad
  const idActividadOtros = Number(economicActivities?.find?.(item => item.value === '10005')?.id) || null

  const matchesReceptorAddress = (district) =>
    district.department === receptor?.department?.value &&
    district.municipality === receptor?.municipality?.value
  let districtOptions = districtsOptions.filter(matchesReceptorAddress)

  if (Array.isArray(districts) && districts.length) {
    districtOptions = districts
  }

  // useForm
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...initialFilterProps
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(SchemaValidations(28000))
  })

  useImperativeHandle(ref, () => ({
    validateForm: () => {
        return trigger(); // Ejecuta todas las validaciones
    },
    resetForm: () => {
      reset(); // Reset the form
      setIsEdited(false)
    }
  }));

  const documentListOptions = documentList?.map(item => ({
    value: item.generationCode,
    label: item.generationCode,
    createdAt: item.emissionDate,
    stampReceived: item.stampReceived,
    receiverName: item.receiverName,
    receiverPhoneNumber: item.receiverPhoneNumber,
    dui: item.dui,
    receiverEmail: item.receiverEmail,
    id: item.id
  }));

  const watchedFields = watch();
  const prevFieldsRef = useRef(watchedFields);

  useEffect(() => {
    const currentFields = watchedFields;
    const prevFields = prevFieldsRef.current;
    if (JSON.stringify(currentFields) !== JSON.stringify(prevFields)) {
      dispatch(setUserAction(currentFields));
    }
    prevFieldsRef.current = currentFields;
  }, [watchedFields, dispatch]);

  useEffect(() => {
    if (invoiceId === 'debit-note') {
      setValue("dteType", { value: '06', label: 'Nota de Débito' })

    }
  }, []);

  const onSubmit = (submitData) => {
    if (isEdited) {
      
      dispatch(editClient({...submitData,
        municipalityId: Number(submitData.municipality?.id),
        departmentId: Number(submitData.department?.id),
        districtId: Number(submitData.district?.id) || null,
        typeEntityId: idTipoPersona('2'),
        largeConsumerId: idContribuyente(submitData?.largeConsumer),
        dteClientTypeId: submitData.dteType?.value === "08" ? "3" : "4", 
        clientCode: "123456789",
        dteTypeId: submitData.dteType?.value === "08" ? "3" : "4",
        company: submitData.receiverName,
        economicActivityId: Number(submitData.economicActivity.id),
        codeTypeId: 1,
        id: clientId
      }))
      .unwrap()
      .then(() => {
        toast.success("Cliente Editado con éxito")
      })
      .catch(error => {
        console.error('Error updating user:', error);
      });
      return

    } else {
      dispatch(addClient({...submitData,
          municipalityId: Number(submitData.municipality?.id),
          departmentId: Number(submitData.department?.id),
          districtId: Number(submitData.district?.id) || null,
          typeEntityId: idTipoPersona('2'), //submitData.typeEntity?.value,
          largeConsumerId: idContribuyente(submitData?.largeConsumer),
          dteClientTypeId: submitData.dteType?.value === "08" ? "3" : "4", // submitData.dteClientType?.value,
          clientCode: "123456789", // validar si es necesario
          dteTypeId: submitData.dteType?.value === "08" ? "3" : "4",
          company: submitData.receiverName,
          economicActivityId: Number(submitData?.economicActivity.id),
          //nit: data.nit,
          dui: submitData.nit,
          //nrc: data.NRC,
          codeTypeId: 1
        }))  
      toast.success("Cliente creado")

    }
  }
  useEffect(() => {
    dispatch(getData({}))

  }, [])

  const enterCustomerFound = async (Client) => {
    setNit((Client.dteType.value === "11" || Client.dteType.value === "14"))
    await reset()

    const clientDocumentType = (
      Client.dteType?.value !== "01" && 
      Client.dteType?.value !== "14" && 
      Client.dteType?.value !== "11") ? 'nit' : "dui"
    const documentNumber = Client.dteType?.value !== "01" ? Client.nit: Client.dui

    /* get documents */
    dispatch(getDocuments({
      documentTypeId: Client.dteType?.value,
      clientDocumentType,
      documentNumber: documentNumber.replace(/[_-]/g, '')
  }))

    const dteTypeSelected = (
      Client.dteType.value === "01" || 
      Client.dteType.value === "11" || 
      Client.dteType.value === "14") ? { value: '08', label: 'Invalidación',  id: "105"} : (invoiceId === 'debit-note' ? { value: '06', label: 'Nota de Débito', id: "7" } : { value: '05', label: 'Nota de Crédito', id: "6" })
    
    dispatch(setUserAction(Client))
    
    setValue('largeConsumer', Client.largeConsumer)
    setValue('nrc', Client.nrc)
    setValue('clientCode', Client.clientCode)
    setValue('dteType', dteTypeSelected)
    setValue('company', Client.company)
    setValue('receiverEmail', Client.receiverEmail)
    setValue('receiverPhoneNumber', Client.receiverPhoneNumber)
    setNameValue(Client.receiverName)
    setValue('receiverName', Client.receiverName)
    setValue('tradeName', Client.tradeName)
    setValue('nit', Client.nit)
    setValue('dui', Client.dui)
    setNITValue(Client.nit)

    setValue('dteClientType', Client.dteClientType)

    setValue('department', Client.department)
    setValue('municipality', Client.municipality)
    setValue('district', Client.district)
    setValue('economicActivity', Client.economicActivity)
    setValue('address', Client.address)
    setFilterProps({...filterProps, dteType: dteTypeSelected.value})

    setValue('emisionType', Client.dteType.value)

    setIsEdited(true)
    setClientId(Client.id)
    
  };

  const economicActivityWithCombinedLabel = (economicActivities || {}).map ? economicActivities.map(option => ({
    ...option,
    label: `${option.value} - ${option.label}`,
  })) : [];

  const { invoiceId } = useParams();
  const filteredOptions = invoicesNotesdb.filter(option => {
    if (invoiceId === 'note') return option.value === '05' || option.value === '08';
    if (invoiceId === 'debit-note') {
      return option.value === '06';

    } 
    return false;
  });

  useEffect(() => {
    if (filterProps?.dteType === '08' && !isNit) {
      setValue('dteClientType', { value: 'dui', label: 'DUI' })
    } else {
      setValue('dteClientType',{ value: 'nit', label: 'NIT' })
    }
  },[filterProps])

  useEffect(() => {
    if (watchedFields?.taxCreditR?.value && (watchedFields?.taxCreditR?.value === watchedFields?.taxCredit?.value)) {
      toast.error(("El campo Factura y Factura de referencia no deben ser iguales"))
      setValue("taxCreditR", null)
    }
  }, [watchedFields?.taxCredit])

  const invoiceValidation = (invoice) => {
    if (invoice === watchedFields?.taxCredit.value) {
      toast.error(("El campo Factura y Factura de referencia no deben ser iguales"))
      setValue("taxCreditR", null)
    }
  }

  const handleSetProducts = async (e) => {
    try {
    //const setProducts = await dispatch(setProductsCreditList({createdAt: e.createdAt, generationCode: e.value}))
    const setProducts = await dispatch(setProductsCreditJsonList({docId: e.id}))
    if (setProducts?.error) {
      toast.error("Factura seleccionada con novedad.")
    }

    } catch (error) {
      toast.error("Factura seleccionada con novedad.")
    }
  }

  const handleClear = () => {
    reset({
      clientCode: '',
      codeType: '',
      dteType: '',
      company: '',
      receiverEmail: '',
      receiverPhoneNumber: '',
      receiverName: '',
      tradeName: '',
      EconomicActivity: null,
      NIT: '',
      DUI: '',
      NRC: '',
      dui:'',
      nit:'',
      nrc:'',
      department: null,
      municipality: null,
      district: null,
      address: '',
      typeEntity: null,
      dteType: null,
      largeConsumer: null,
      dteClientType: null,
      economicActivity: null,
      country: null
    })
    setValue('NIT', "")
    setValue('NRC', "")
    setValue('tradeName', "")
    
    setIsEdited(false)
    
  }

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex-md-row flex-column align-md-items-center align-items-start border-bottom">
            <CardTitle tag="h4">Informacion del cliente</CardTitle>
          </CardHeader>
          <Row className="mx-0" style={{ paddingTop: '2%', paddingLeft: '5%' }}>
            <Col
                >
                <SearchInput itemList={clientsdb} 
                      label={"Nombre de cliente o empresa"}
                      defaultvalueInput={nameValue}
                      errors={errors}
                      control={control}
                      action={enterCustomerFound}
                      searchType={"receiverName"}
                      database={data}
                      options={{
                        id: 'receiverName',
                        name: 'receiverName',
                        }}
                    />
            </Col>
            <Col>
              <Label
              className='me-1'
              >Tipo de documento del cliente</Label>
              <Controller
                name='dteClientType'
                control={control}
                render={({field}) => (
                    <>
                        <Select
                        {...field}
                        options={[
                            { value: 'dui', label: 'DUI' },
                            { value: 'nit', label: 'NIT' },
                        ]}
                        isDisabled={true}
                        />
                        {errors.dteClientType && <FormFeedback className="d-block">{errors.dteClientType.message}</FormFeedback>}
                    </>


                )}
              />
            </Col>
            {
            filterProps?.dteType !== '08' ? ( <>
                <Col
                >
                    <SearchInput itemList={clientsdb} 
                      label={"NIT"}
                      errors={errors}
                      control={control}
                      searchType={"NIT"}
                      database={data}
                      action={enterCustomerFound}
                      options={{
                        id: 'nit',
                        name: 'nit',
                        }}
                        mascara={"nit"}
                    />
                </Col>
                <Col
                    >
                    <Label className="me-1">NRC*</Label>
                      <Controller
                        id="nrc"
                        name="nrc"
                        control={control}
                        rules={{
                          required: "El NRC es requerido",
                          pattern: {
                            value: /^[0-9]{1,8}$/,
                            message:
                              "El NRC debe tener entre 1 y 8 dígitos numéricos",
                          },
                        }}
                        render={({ field }) => {
                          return (
                            <Cleave
                              options={{
                                numericOnly: true, // Solo permite números
                                delimiter: "-",
                                blocks: [8],
                              }}
                              className={classnames("form-control")}
                              {...field}
                            />
                          );
                        }}
                      />
                      <FormFeedback className="d-block">
                        {errors?.nrc?.message}
                      </FormFeedback>
                    </Col>
                </>  
              ) :(
                isNit ? (<Col
                >
                    <SearchInput itemList={clientsdb} 
                      label={"NIT"}
                      errors={errors}
                      control={control}
                      searchType={"NIT"}
                      database={data}
                      action={enterCustomerFound}
                      options={{
                        id: 'nit',
                        name: 'nit',
                        }}
                        mascara={null}
                    />
                </Col>
                ):(
                <Col>
                <SearchInput itemList={clientsdb} 
                  label={"DUI"}
                  errors={errors}
                  control={control}
                  searchType={"dui"}
                  database={data}
                  action={enterCustomerFound}
                  options={{
                    id: 'dui',
                    name: 'dui',
                    }}
                    mascara={"dui"}
                />
            </Col>)           
           )
          }
          
          </Row>
          <Row className="mx-0" style={{ paddingTop: '2%', paddingLeft: '5%' }}>
          <Col  md="4" sm="12">
              <Label
              className='me-1'
              >Tipo de documento a emitir</Label>
              <Controller
                name='dteType'
                control={control}
                render={({field}) => (
                  <>
                    <Select
                    {...field}
                    isDisabled={isEdited}
                    options={filteredOptions}
                    //value={filteredOptions.length === 1 ? filteredOptions[0] : null}
                    onChange={(e) => {
                        setFilterProps({ ...filterProps, dteType: e.value})
                        //field.onChange(e.value)
                        field.onChange({value: e.value, label: e.label, id: e.id})

                    }}
                    />
                    {errors.dteType && <FormFeedback className="d-block">{errors.dteType.message}</FormFeedback>}
                  </>
                  
                )}
                
              />
            </Col>
            

            <Col  md="4" sm="12">
              <Label
              className='me-1'
              >{filterProps?.dteType === '08' ? "Factura*" : "Crédito Fiscal*"}</Label>
              <Controller
                name='taxCredit'
                control={control}
                render={({field}) => (
                    <>
                        <Select
                        {...field}
                        options={isEdited ? documentListOptions : []}
                        onChange={(e) => {
                            field.onChange({
                              label: e.value, 
                              value: e.value, stampReceived: e.stampReceived,
                              receiverName: e.receiverName,
                              receiverPhoneNumber: e.receiverPhoneNumber,
                              dui: e.dui,
                              receiverEmail: e.receiverEmail,
                              id: e.id
                            })
                            //dispatch(setProductsCreditList({createdAt: e.createdAt, generationCode: e.value}))
                            handleSetProducts(e)
                        }}
                        />
                        {errors.taxCredit && <FormFeedback className="d-block">{errors.taxCredit.message}</FormFeedback>}
                    </>


                )}
              />
            </Col>

            {
              filterProps?.dteType === '08' && false &&
              <Col  md="4" sm="12">
              <Label
              className='me-1'
              >Factura de referencia*</Label>
              <Controller
                name='taxCreditR'
                control={control}
                render={({field}) => (
                    <>
                        <Select
                        {...field}
                        isDisabled={ !watchedFields?.taxCredit}
                        options={isEdited ? documentListOptions : []}
                        onChange={(e) => {
                            //dispatch(setProductsCreditList({createdAt: e.createdAt, generationCode: e.value}))
                            field.onChange({label: e.value, value: e.value})
                            invoiceValidation(e.value)
                        }}
                        />
                        {errors.taxCredit && <FormFeedback className="d-block">{errors.taxCredit.message}</FormFeedback>}
                    </>


                )}
              />
            </Col>
            }
            
          </Row>
          <Row className="mx-0" style={{ paddingTop: '2%', paddingLeft: '5%' }}>
          {
                filterProps?.dteType !== '01' &&
            <>
            <Col>
                <Label className="me-1">Nombre comercial*</Label>
                <Controller
                  name="tradeName"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        errors={errors}
                        options={{
                          control,
                          id: "tradeName",
                          name: "tradeName",
                          // disabled: true
                        }}
                      />
                      {errors.tradeName && (
                        <FormFeedback className="d-block">
                          {errors.tradeName.message}
                        </FormFeedback>
                      )}
                    </>
                  )}
                />
              </Col>
            
            
            <Col >
              <Label
              className='me-1'
              >Actividad económica</Label>
              <Controller
                name='economicActivity'
                control={control}
                render={({field}) => (
                    <>
                        <Select
                        {...field}
                        options={economicActivityWithCombinedLabel}
                        />
                        {errors.EconomicActivity && <FormFeedback className="d-block">{errors.EconomicActivity.message}</FormFeedback>}
                    
                    </>
                  
                )}
              />
            </Col>
            </>
          }
          </Row>
          
          <Row className="mx-0" style={{ paddingTop: '2%', paddingLeft: '5%' }}>
 
            <Col
                md="6"
                sm="12"
                >
              <InputInternationalPhone
                  label={"Teléfono*"}
                  errors={errors}
                  control={control}
                  options={{
                    id: "receiverPhoneNumber",
                    name: "receiverPhoneNumber",
                  }}
                />
            </Col>
            
            <Col
              md="6"
              sm="12"
            >
                
              <InputEmail
                label="Correo Electrónico"
                errors={errors}
                control={control}
                options={{
                  id: 'receiverEmail',
                  name: 'receiverEmail'
                }}
              />
            </Col>
          </Row>
          {
            (filterProps?.dteType !== '08') &&
            <>
                <Row className="mx-0" style={{paddingTop: "2%", paddingLeft: '5%' }}>
                    <Col  md="6" sm="12">
                        <Label
                        className='me-1'
                        >Departamento</Label>
                        <Controller
                            name='department'
                            control={control}
                            render={({field}) => (
                                <>
                                <Select
                                    {...field}
                                    options={departments}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      setValue('municipality', null)
                                      setValue('district', null)
                                    }}
                                    />
                                {errors.department && <FormFeedback className="d-block">{errors.department.message}</FormFeedback>}
                                </>

                            )}
                        />
                    </Col>
                    <Col  md="6" sm="12">
                        <Label
                        className='me-1'
                        >Municipio</Label>
                        <Controller
                            name='municipality'
                            control={control}
                            render={({field}) => (
                                <>
                                <Select
                                    {...field}
                                    options={cities}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      setValue('district', null)
                                    }}
                                    />
                                {errors.municipality && <FormFeedback className="d-block">{errors.municipality.message}</FormFeedback>}
                                </>
                            )}
                        />
                    </Col>
                    <Col  md="6" sm="12">
                        <Label
                        className='me-1'
                        >Distrito</Label>
                        <Controller
                            name='district'
                            control={control}
                            render={({field}) => (
                                <>
                                <Select
                                    {...field}
                                    options={districtOptions}
                                    isDisabled={!watchedFields?.municipality?.value}
                                    />
                                {errors.district && <FormFeedback className="d-block">{errors.district.message}</FormFeedback>}
                                </>
                            )}
                        />
                    </Col>
                    </Row>
                    <Row  className="mx-0" style={{paddingTop: "2%", paddingLeft: '5%' }}>
                    <Col
                            md="6"
                            sm="12"
                            >
                            <InputText
                                label={"Dirección"}
                                errors={errors}
                                options={{
                                control,
                                id: 'address',
                                name: 'address'
                                }}
                            />
                        </Col>
                        <Col>
                          <Label className="me-1">Tipo de contribuyente</Label>
                          <Controller
                            name="largeConsumer"
                            control={control}
                            render={({ field }) => (
                              <>
                                <Select {...field} options={contributorOptions} />
                                {errors.largeConsumer && (
                                  <FormFeedback className="d-block">
                                    {errors.largeConsumer.message}
                                  </FormFeedback>
                                )}
                              </>
                            )}
                          />
                        </Col>
                    </Row>
            </>
          }
          

          <Row className="mx-0" style={{ paddingLeft: '5%' }}>
          {isEdited && (
              <Col className="d-flex align-items-center  justify-content-start my-3">
                <Button
                  style={{ size: "10px", width: "200px" }}
                  type="reset"
                  color="secondary"
                  outline
                  onClick={handleClear}
                >
                  Limpiar
                </Button>
              </Col>
            )}
            <Col className="d-flex align-items-left justify-content-sm-left my-3">
              <Button
                style={{ size: '10px', width: '200px', display: "none"}}
                color="primary"
                type="submit"
                //onClick={handleClearFilters}
              >
                {
                  isEdited ? "Editar Cliente" : "Crear Cliente"
                }
                
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
    </Fragment>
  )
})

export default CreateUserCreditDebitForm